import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { Row, Alert } from 'reactstrap';
import Well from 'components/Well';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import CheckInDetailsContainer from 'containers/CheckInDetailsContainer';
import RoomForm from './RoomForm';
import PropTypes from 'prop-types';
import RoomList from './RoomList';
import { isMobileUserAgent } from 'utils/mobile';
import { getBookingEnginePath, isHostBookingSource } from 'utils/routeUtils';
import BackButtonContainer from 'containers/BackButtonContainer';
import LargestCol from 'components/LargestCol';
import gql from 'graphql-tag';
import { Query } from 'react-apollo';
import SpinnerContainer from 'routes/DateSelection/components/styled/SpinnerContainer';
import moment from 'moment';
import {
  PROPERTY_CODES,
  BOOKING_ENGINE_PAGE_CHANGED,
  PROPERTY_WETUMPKA
} from 'utils/constants';
import { createSelector } from 'reselect';
import filter from 'lodash/filter';
import { ROOMS } from 'utils/constants';
import fragments from 'utils/graphql/fragments';
import {
  blockShape,
  selectedOfferShape,
  submittedAccountShape
} from 'utils/shapes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addPropertyImagePrefix } from 'utils/images';
import ErrorModal from 'components/ErrorModal';
import { NON_SMOKING, SMOKING } from 'utils/constants';
import HostBookingDetailsContainer from 'containers/HostBookingDetailsContainer';

const QUERY_GET_AVAILABILITY = gql`
  query GetRoomAvailability($input: GetRoomAvailability!) {
    roomAvailability(input: $input) {
      rooms {
        roomType {
          roomTypeDescription
          roomTypeShortDescription
          amenityInfo {
            amenities {
              amenityDescription
              amenityCode
              amenityType
              otherType
              availabilityFlag
              otherFlag
            }
          }
          roomFeatures {
            id
            feature
            description
            attachPreferenceToProfile
          }
          roomNumber
          roomToChargeDescription
          roomToChargeShortDescription
          roomClass
          feature
          numberOfUnits
          isRoom
          effectiveDate
          expirationDate
          roomTypeName
          roomToChargeName
          roomStatus
          pseudoRoom
          roomServiceStatus
          minimumRoomsAvailable
          roomTypeCodes {
            roomTypeCode
          }
        }
        roomRates {
          rates {
            base {
              amount
            }
          }
          total {
            amount
            currencyCode
          }
          roomTypeCode
          ratePlanCode
          eligibleForComp
          expectedCharges {
            chargesForPostingDate {
              roomRateAndPackages {
                totalCharges
              }
              taxesAndFees {
                totalCharges
              }
              postingDate
            }
            totalRoomAndRatePackages
            totalTaxesAndFees
          }
        }
      }
      rewards {
        ...HotelOfferFields
      }
      error
    }
  }
  ${fragments.HotelOfferFields}
`;

const AccommodationCol = styled(LargestCol)`
  width: 100%;

  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    height: 100%;
  }

  .row {
    align-items: flex-end;
  }

  ${Well} h1 {
    margin: 0 0 0.5em 0;
  }

  .atmore-header,
  .bethlehem-header,
  .montgomery-header,
  .wetumpka-header {
    background-size: contain;
    background-repeat: no-repeat;
    padding-top: 7em;

    @media (min-width: ${props =>
      props.theme.breakpoints.small}) and (max-width: ${props =>
  props.theme.breakpoints.large}) {
        padding-top: 11em;
    }

    p {
      line-height: 0.9em;
      margin: 0;
    }
  }

  .atmore-header {
    background-image: url('${addPropertyImagePrefix(
      'atmore/AtmoreHeader.png'
    )}');
  }

  .bethlehem-header {
    background-image: url('${addPropertyImagePrefix(
      'bethlehem/BethlehemHeader.png'
    )}');
  }

  .montgomery-header {
    background-image: url('${addPropertyImagePrefix(
      'montgomery/MontgomeryHeader.png'
    )}');
  }

  .wetumpka-header {
    background-image: url('${addPropertyImagePrefix(
      'wetumpka/WetumpkaHeader.png'
    )}');
  }

  @media (max-width: ${props => props.theme.breakpoints.extraExtraLarge}) {
    margin-bottom: 0;

    ${props => props.theme.center};
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.medium}) {
    display: flex;

    ${Well} {
      flex: 1 0 auto;
      width: 100%;
    }
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
    ${Well} {
      margin-bottom: 0;
    }
  }
`;

const RoomTypeCol = styled(LargestCol)`
  max-height: 100%;
  display: flex;
  flex-direction: column;

  ${SpinnerContainer} {
    position: static;
    width: 100%;
    height: 100%;
    flex: 0 1 auto;
    padding: 1em;
  }

  @media (max-width: ${props => props.theme.breakpoints.extraExtraLarge}) {
    margin: ${props => props.theme.center};
  }
`;

const HeaderCol = styled(LargestCol)`
  text-align: center;
  margin-bottom: 1em;

  & > h1,
  & > h2 {
    font-size: 1.5em;
  }

  @media screen and (min-width: ${props => props.theme.breakpoints.small}) {
    & > h1,
    & > h2 {
      font-size: 2rem;
    }
  }

  @media screen and (min-width: 0px) {
    &[class*='col-'] {
      &[class*='-2'] {
        flex: 0 0 100%;
        max-width: 100%;
      }
    }
  }
`;

const ChooseAccommodationCol = styled(LargestCol)`
  display: flex;
  flex-direction: column;
  max-height: 100%;

  .row {
    flex: 0 1 auto;
  }

  .row.body-row {
    flex: 0 1 auto;
    min-height: 0;

    @media screen and (min-width: ${props => props.theme.breakpoints.large}) {
      height: 100%;
    }
  }

  @media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
    .row.body-row {
      flex: 0 0 auto;
    }
  }

  @media screen and (min-width: 0px) {
    &[class*='col-'] {
      &[class*='-3'] {
        flex: 0 0 100%;
        max-width: 100%;
      }
    }
  }
`;

const StyledAlert = styled(Alert)`
  font-size: 0.8em;

  h4 {
    font-size: 1.1em;
  }

  p {
    margin: 0;
  }
`;

class ChooseAccommodation extends Component {
  constructor(props) {
    super(props);

    const { startDate, property } = props;

    this.state = {
      showAlert:
        property === PROPERTY_WETUMPKA &&
        moment(startDate).isSameOrBefore('2020-04-30'),
      isMobile: false
    };
  }

  filterRooms = (rooms, property) =>
    createSelector(
      [() => this.props.filters],
      filters =>
        filter(rooms, room => {
          const roomTypeCode = room.roomType.roomTypeCodes.roomTypeCode;
          const roomInfo = ROOMS[property][roomTypeCode];

          if (!roomInfo) {
            return false;
          }

          const roomTypeFilter =
            filters.roomType && filters.roomType.label !== 'All'
              ? roomInfo.roomType === filters.roomType.value
              : true;
          const smokingFilter =
            filters && filters.smoking && filters.smoking === SMOKING
              ? roomInfo.smoking
              : true;
          const nonsmokingFilter =
            filters && filters.smoking && filters.smoking === NON_SMOKING
              ? !roomInfo.smoking
              : true;
          const accessibilityFilter =
            filters && filters.accessible
              ? roomInfo.accessible === filters.accessible
              : true;

          return (
            roomTypeFilter &&
            smokingFilter &&
            accessibilityFilter &&
            nonsmokingFilter
          );
        })
    );

  resetCalendar = () => {
    const { setQueryInput, startDate } = this.props;

    setQueryInput(
      moment(startDate)
        .startOf('month')
        .format('YYYY-MM-DD')
    );
  };

  closeAlert = () => {
    this.setState({ showAlert: false });
  };

  componentDidMount() {
    const isMobile = isMobileUserAgent();
    const parentWindow = window.parent;

    if (isMobile) {
      this.handleMobileEvent(isMobile);
      window.addEventListener('mobile', this.handleMobileEvent);
    }

    parentWindow.postMessage(BOOKING_ENGINE_PAGE_CHANGED, '*');
  }

  componentWillUnmount() {
    if (window.removeEventListener) {
      window.removeEventListener('mobile', this.handleMobileEvent);
    }
  }

  handleMobileEvent = mobile => {
    const { isMobile } = this.state;

    if (isMobile !== mobile) {
      this.setState({
        isMobile: mobile
      });
    }
  };

  render() {
    const {
      className,
      selectRoom,
      numberOfNights,
      updateFilters,
      filters,
      numberOfGuests,
      startDate,
      endDate,
      property,
      submittedAccount,
      selectedOffers,
      shouldRefetch,
      setShouldRefetch,
      isAdmin,
      activeBlock,
      bookingSource,
      secondSubmittedAccount,
      isWaitListBooking,
      waitListBookingReason
    } = this.props;

    const { showAlert, isMobile } = this.state;

    // Redirect to beginning if dates aren't selected.
    if (!numberOfNights) {
      return <Redirect to={getBookingEnginePath()} />;
    }

    const formattedStartDate = moment(startDate).format('YYYY-MM-DD');
    const formattedEndDate = moment(endDate).format('YYYY-MM-DD');

    const queryInput = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      propertyCode: PROPERTY_CODES[property]
    };

    if (activeBlock?.code) {
      queryInput.blockCode = activeBlock.code;
    } else {
      if (submittedAccount.accountNumber) {
        queryInput.patronAccountNumber = submittedAccount.accountNumber;
      }

      if (!isAdmin && submittedAccount.accountPin) {
        queryInput.pin = submittedAccount.accountPin;
      }

      if (secondSubmittedAccount) {
        queryInput.secondPatronAccountNumber =
          secondSubmittedAccount.accountNumber;
      }
    }

    if (selectedOffers && selectedOffers.length > 0) {
      queryInput.promotionCodes = selectedOffers;
    }

    const isHostBooking = isHostBookingSource(bookingSource);

    if (isHostBooking) {
      queryInput.isHostBooking = true;
    }

    if (waitListBookingReason) {
      queryInput.waitlistReasonCode = waitListBookingReason.value;
    }

    return (
      <div className={className}>
        <Helmet>
          <title>Choose Accommodations</title>
        </Helmet>
        <Row>
          <ChooseAccommodationCol
            md={12}
            lg={isHostBooking && !isMobile ? 9 : 12}
          >
            <Row className="header-row">
              <HeaderCol xs="12">
                <h1>CHOOSE YOUR WIND CREEK ACCOMMODATION</h1>
                {!(isHostBooking && isMobile) && (
                  <p>
                    Based on your search criteria, the following options are
                    available.
                  </p>
                )}
              </HeaderCol>
            </Row>
            <Row className="body-row">
              <AccommodationCol md="12" lg="4">
                <Well className={`${property}-header`}>
                  <CheckInDetailsContainer hideNumberOfGuests />
                  <RoomForm
                    onChange={updateFilters}
                    initialValues={{ numberOfGuests, ...filters }}
                    property={property}
                    isHostBooking={isHostBooking}
                  />
                </Well>
              </AccommodationCol>
              <RoomTypeCol md="12" lg="8">
                {showAlert && (
                  <StyledAlert
                    isOpen={showAlert}
                    color="danger"
                    toggle={this.closeAlert}
                  >
                    <h4>Construction</h4>
                    <p>
                      The Hotel’s second floor is currently under construction
                      between 9am and 6pm the next several months for our
                      upcoming Spa and hotel renovation. Please be advised that
                      you might experience some noise during your stay.
                      Additionally, the Business Center and Fitness Room –
                      located on the second floor – are temporarily closed.
                    </p>
                  </StyledAlert>
                )}
                <Query
                  query={QUERY_GET_AVAILABILITY}
                  skip={!startDate || !endDate || !property}
                  variables={{ input: queryInput }}
                >
                  {({ loading, error, data, refetch }) => {
                    if (loading) {
                      return (
                        <SpinnerContainer>
                          <FontAwesomeIcon icon="spinner" size="5x" spin />
                        </SpinnerContainer>
                      );
                    }

                    if (!loading && shouldRefetch) {
                      refetch();
                      setShouldRefetch(false);
                    }

                    const filteredRooms =
                      data && data.roomAvailability
                        ? this.filterRooms(
                            data.roomAvailability.rooms,
                            property
                          )()
                        : [];

                    const rewards =
                      data &&
                      data.roomAvailability &&
                      data.roomAvailability.rewards;

                    const errorMessage =
                      data && data.roomAvailability
                        ? data.roomAvailability.error
                        : '';

                    return (
                      <React.Fragment>
                        <RoomList
                          onSelect={selectRoom}
                          rooms={filteredRooms}
                          nights={numberOfNights}
                          property={property}
                          rewards={rewards}
                          bookingSource={bookingSource}
                          isWaitListBooking={isWaitListBooking}
                          error={errorMessage}
                          waitListBookingReason={waitListBookingReason}
                          isHostBooking={isHostBooking}
                        />
                        {error && (
                          <ErrorModal
                            property={property}
                            isOpen={true}
                            error={error}
                          />
                        )}
                      </React.Fragment>
                    );
                  }}
                </Query>
              </RoomTypeCol>
            </Row>
          </ChooseAccommodationCol>
          {isHostBooking && (
            <LargestCol md="12" lg="3">
              <HostBookingDetailsContainer
                isAdmin={isAdmin}
                isHostBooking={isHostBooking}
                hideGuestInfo={isMobile}
                hideBookYourStay={isMobile}
              />
            </LargestCol>
          )}
        </Row>
        <Row>
          <LargestCol xs="12">
            <BackButtonContainer
              beforeNav={this.resetCalendar}
              to={getBookingEnginePath()}
            />
          </LargestCol>
        </Row>
      </div>
    );
  }
}

ChooseAccommodation.propTypes = {
  filteredRooms: PropTypes.array,
  selectedRoom: PropTypes.object,
  numberOfNights: PropTypes.number,
  selectRoom: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  filters: PropTypes.object,
  numberOfGuests: PropTypes.object,
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  property: PropTypes.string,
  submittedAccount: submittedAccountShape,
  selectedOffers: PropTypes.arrayOf(selectedOfferShape),
  setQueryInput: PropTypes.func,
  shouldRefetch: PropTypes.bool,
  setShouldRefetch: PropTypes.func,
  isAdmin: PropTypes.bool,
  bookingSource: PropTypes.string,
  activeBlock: blockShape,
  secondSubmittedAccount: submittedAccountShape,
  isWaitListBooking: PropTypes.bool,
  waitListBookingReason: PropTypes.shape({
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired
  })
};

export default ChooseAccommodation;
